import React, { useState, useEffect } from 'react';
import {
    Card,
    Text,
    Button,
    Tab,
    TabList,
    Field,
    Input,
    Dropdown,
    Option,
    Checkbox,
    RadioGroup,
    Radio,
    Spinner,
    Slider,
    Badge,
    Divider,
    Switch,
    MessageBar,
    MessageBarBody,
    ProgressBar,
} from '@fluentui/react-components';
import {
    Settings24Regular,
    Clock24Regular,
    Person24Regular,
    Alert24Regular,
    Save24Regular,
    ArrowCounterclockwise24Regular,
    Checkmark24Regular,
    Warning24Regular,
} from '@fluentui/react-icons';
import { apiService } from '../../../services/api/apiService';

interface WorkingHours {
    [key: string]: {
        start: string;
        end: string;
        enabled: boolean;
    };
}

interface ConsultationPreferences {
    preferredTypes: string[];
    maxConsecutiveConsults: number;
    bufferTimeBetween: number;
    allowBackToBack: boolean;
    emergencyConsults: boolean;
    childPatients: boolean;
    adultPatients: boolean;
    geriatricPatients: boolean;
    addictionPatients: boolean;
}

interface NotificationPreferences {
    email: {
        newAssignment: boolean;
        scheduleChanges: boolean;
        emergencyConsults: boolean;
        dailySchedule: boolean;
        weeklySchedule: boolean;
    };
    teams: {
        newAssignment: boolean;
        urgentConsults: boolean;
        scheduleReminders: boolean;
        patientWaiting: boolean;
    };
    mobile: {
        emergencyOnly: boolean;
        scheduleReminders: boolean;
    };
}

interface UIPreferences {
    theme: 'light' | 'dark';
    dashboardLayout: 'standard' | 'compact' | 'detailed';
    calendarView: 'day' | 'week' | 'month';
    autoRefresh: boolean;
    notificationSounds: boolean;
    compactView: boolean;
}

interface DoctorPreferencesData {
    id: string;
    name: string;
    specialty: string;
    timeZone: string;
    workingHours: WorkingHours;
    consultationPreferences: ConsultationPreferences;
    notificationPreferences: NotificationPreferences;
    uiPreferences: UIPreferences;
    maxDailyConsultations: number;
    emergencyAvailable: boolean;
    preferredLanguages: string[];
    breakPreferences: any;
    preferences: any;
}

export const DoctorPreferences: React.FC = () => {
    const [activeTab, setActiveTab] = useState<string>('schedule');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [preferences, setPreferences] = useState<DoctorPreferencesData | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [message, setMessage] = useState<{
        type: 'success' | 'error' | 'warning';
        text: string;
    } | null>(null);

    const daysOfWeek = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
    ];
    const dayLabels = {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
    };

    const consultationTypes = [
        'general',
        'follow-up',
        'crisis',
        'evaluation',
        'medication-review',
        'therapy',
        'consultation',
    ];

    const timeZones = [
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Phoenix',
        'America/Anchorage',
    ];

    const languages = [
        'English',
        'Spanish',
        'French',
        'German',
        'Italian',
        'Portuguese',
        'Chinese',
        'Japanese',
        'Korean',
        'Arabic',
    ];

    useEffect(() => {
        loadPreferences();
    }, []);

    const loadPreferences = async () => {
        try {
            setLoading(true);
            const response = await apiService.getDoctorPreferences();
            setPreferences(response.preferences);
            setHasChanges(false);
        } catch (error) {
            console.error('Error loading preferences:', error);
            setMessage({ type: 'error', text: 'Failed to load preferences' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!preferences) return;

        try {
            setSaving(true);
            await apiService.updateDoctorPreferences(preferences);
            setMessage({ type: 'success', text: 'Preferences saved successfully!' });
            setHasChanges(false);
        } catch (error) {
            console.error('Error saving preferences:', error);
            setMessage({ type: 'error', text: 'Failed to save preferences' });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        try {
            setSaving(true);
            await apiService.resetDoctorPreferences();
            await loadPreferences();
            setMessage({ type: 'success', text: 'Preferences reset to defaults!' });
        } catch (error) {
            console.error('Error resetting preferences:', error);
            setMessage({ type: 'error', text: 'Failed to reset preferences' });
        } finally {
            setSaving(false);
        }
    };

    const updatePreferences = (path: string[], value: any) => {
        if (!preferences) return;

        const newPreferences = { ...preferences };
        let current: any = newPreferences;

        for (let i = 0; i < path.length - 1; i++) {
            current = current[path[i]];
        }
        current[path[path.length - 1]] = value;

        setPreferences(newPreferences);
        setHasChanges(true);
    };

    const renderScheduleTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Time Zone */}
            <Card style={{ padding: '20px' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                    }}
                >
                    <Clock24Regular style={{ fontSize: '20px', color: '#0078d4' }} />
                    <Text size={600} weight="semibold">
                        Time Zone & Capacity
                    </Text>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    <Field label="Time Zone">
                        <Dropdown
                            value={preferences?.timeZone}
                            onOptionSelect={(_, data) =>
                                updatePreferences(['timeZone'], data.optionValue)
                            }
                        >
                            {timeZones.map((tz) => (
                                <Option key={tz} value={tz}>
                                    {tz}
                                </Option>
                            ))}
                        </Dropdown>
                    </Field>

                    <Field label="Max Daily Consultations">
                        <Input
                            type="number"
                            min={1}
                            max={20}
                            value={preferences?.maxDailyConsultations?.toString() || ''}
                            onChange={(_, data) =>
                                updatePreferences(['maxDailyConsultations'], parseInt(data.value))
                            }
                        />
                    </Field>

                    <Field label="Emergency Availability">
                        <Switch
                            checked={preferences?.emergencyAvailable}
                            onChange={(_, data) =>
                                updatePreferences(['emergencyAvailable'], data.checked)
                            }
                        />
                    </Field>
                </div>
            </Card>

            {/* Working Hours */}
            <Card style={{ padding: '20px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '16px' }}>
                    Weekly Schedule
                </Text>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {daysOfWeek.map((day) => (
                        <div
                            key={day}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '120px 1fr 100px 100px',
                                gap: '16px',
                                alignItems: 'center',
                                padding: '12px',
                                backgroundColor: preferences?.workingHours[day]?.enabled
                                    ? '#f8f9fa'
                                    : '#f0f0f0',
                                borderRadius: '8px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Checkbox
                                    checked={preferences?.workingHours[day]?.enabled || false}
                                    onChange={(_, data) =>
                                        updatePreferences(
                                            ['workingHours', day, 'enabled'],
                                            data.checked
                                        )
                                    }
                                />
                                <Text weight="medium">
                                    {dayLabels[day as keyof typeof dayLabels]}
                                </Text>
                            </div>

                            <div></div>

                            <Field label="Start">
                                <Input
                                    type="time"
                                    value={preferences?.workingHours[day]?.start || '08:00'}
                                    disabled={!preferences?.workingHours[day]?.enabled}
                                    onChange={(_, data) =>
                                        updatePreferences(
                                            ['workingHours', day, 'start'],
                                            data.value
                                        )
                                    }
                                />
                            </Field>

                            <Field label="End">
                                <Input
                                    type="time"
                                    value={preferences?.workingHours[day]?.end || '17:00'}
                                    disabled={!preferences?.workingHours[day]?.enabled}
                                    onChange={(_, data) =>
                                        updatePreferences(['workingHours', day, 'end'], data.value)
                                    }
                                />
                            </Field>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Break Preferences */}
            <Card style={{ padding: '20px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '16px' }}>
                    Break Preferences
                </Text>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <Field label="Lunch Break Start">
                        <Input
                            type="time"
                            value={preferences?.breakPreferences?.lunchBreak?.start || '12:00'}
                            onChange={(_, data) =>
                                updatePreferences(
                                    ['breakPreferences', 'lunchBreak', 'start'],
                                    data.value
                                )
                            }
                        />
                    </Field>

                    <Field label="Lunch Break End">
                        <Input
                            type="time"
                            value={preferences?.breakPreferences?.lunchBreak?.end || '13:00'}
                            onChange={(_, data) =>
                                updatePreferences(
                                    ['breakPreferences', 'lunchBreak', 'end'],
                                    data.value
                                )
                            }
                        />
                    </Field>

                    <Field label="Minimum Time Between Consultations (minutes)">
                        <Slider
                            min={0}
                            max={30}
                            step={5}
                            value={preferences?.breakPreferences?.betweenConsults?.minimum || 5}
                            onChange={(_, data) =>
                                updatePreferences(
                                    ['breakPreferences', 'betweenConsults', 'minimum'],
                                    data.value
                                )
                            }
                        />
                        <Text size={300} style={{ color: '#605e5c' }}>
                            {preferences?.breakPreferences?.betweenConsults?.minimum || 5} minutes
                        </Text>
                    </Field>

                    <Field label="Preferred Time Between Consultations (minutes)">
                        <Slider
                            min={5}
                            max={60}
                            step={5}
                            value={preferences?.breakPreferences?.betweenConsults?.preferred || 10}
                            onChange={(_, data) =>
                                updatePreferences(
                                    ['breakPreferences', 'betweenConsults', 'preferred'],
                                    data.value
                                )
                            }
                        />
                        <Text size={300} style={{ color: '#605e5c' }}>
                            {preferences?.breakPreferences?.betweenConsults?.preferred || 10}{' '}
                            minutes
                        </Text>
                    </Field>
                </div>
            </Card>
        </div>
    );

    const renderConsultationTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Consultation Types */}
            <Card style={{ padding: '20px' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                    }}
                >
                    <Person24Regular style={{ fontSize: '20px', color: '#0078d4' }} />
                    <Text size={600} weight="semibold">
                        Consultation Preferences
                    </Text>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <Text size={500} weight="medium" style={{ marginBottom: '12px' }}>
                        Preferred Consultation Types
                    </Text>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '12px',
                        }}
                    >
                        {consultationTypes.map((type) => (
                            <Checkbox
                                key={type}
                                label={
                                    type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')
                                }
                                checked={
                                    preferences?.consultationPreferences?.preferredTypes?.includes(
                                        type
                                    ) || false
                                }
                                onChange={(_, data) => {
                                    const currentTypes =
                                        preferences?.consultationPreferences?.preferredTypes || [];
                                    const newTypes = data.checked
                                        ? [...currentTypes, type]
                                        : currentTypes.filter((t) => t !== type);
                                    updatePreferences(
                                        ['consultationPreferences', 'preferredTypes'],
                                        newTypes
                                    );
                                }}
                            />
                        ))}
                    </div>
                </div>

                <Divider />

                <div
                    style={{
                        marginTop: '20px',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '20px',
                    }}
                >
                    <Field label="Max Consecutive Consultations">
                        <Slider
                            min={1}
                            max={8}
                            step={1}
                            value={
                                preferences?.consultationPreferences?.maxConsecutiveConsults || 4
                            }
                            onChange={(_, data) =>
                                updatePreferences(
                                    ['consultationPreferences', 'maxConsecutiveConsults'],
                                    data.value
                                )
                            }
                        />
                        <Text size={300} style={{ color: '#605e5c' }}>
                            {preferences?.consultationPreferences?.maxConsecutiveConsults || 4}{' '}
                            consultations
                        </Text>
                    </Field>

                    <Field label="Buffer Time Between Consultations (minutes)">
                        <Slider
                            min={5}
                            max={60}
                            step={5}
                            value={preferences?.consultationPreferences?.bufferTimeBetween || 15}
                            onChange={(_, data) =>
                                updatePreferences(
                                    ['consultationPreferences', 'bufferTimeBetween'],
                                    data.value
                                )
                            }
                        />
                        <Text size={300} style={{ color: '#605e5c' }}>
                            {preferences?.consultationPreferences?.bufferTimeBetween || 15} minutes
                        </Text>
                    </Field>
                </div>
            </Card>

            {/* Patient Types */}
            <Card style={{ padding: '20px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '16px' }}>
                    Patient Population Preferences
                </Text>

                <div
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}
                >
                    <Checkbox
                        label="Child Patients"
                        checked={preferences?.consultationPreferences?.childPatients || false}
                        onChange={(_, data) =>
                            updatePreferences(
                                ['consultationPreferences', 'childPatients'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Adult Patients"
                        checked={preferences?.consultationPreferences?.adultPatients || false}
                        onChange={(_, data) =>
                            updatePreferences(
                                ['consultationPreferences', 'adultPatients'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Geriatric Patients"
                        checked={preferences?.consultationPreferences?.geriatricPatients || false}
                        onChange={(_, data) =>
                            updatePreferences(
                                ['consultationPreferences', 'geriatricPatients'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Addiction Patients"
                        checked={preferences?.consultationPreferences?.addictionPatients || false}
                        onChange={(_, data) =>
                            updatePreferences(
                                ['consultationPreferences', 'addictionPatients'],
                                data.checked
                            )
                        }
                    />
                </div>

                <Divider style={{ margin: '20px 0' }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Checkbox
                        label="Accept Emergency Consultations"
                        checked={preferences?.consultationPreferences?.emergencyConsults || false}
                        onChange={(_, data) =>
                            updatePreferences(
                                ['consultationPreferences', 'emergencyConsults'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Allow Back-to-Back Consultations"
                        checked={preferences?.consultationPreferences?.allowBackToBack || false}
                        onChange={(_, data) =>
                            updatePreferences(
                                ['consultationPreferences', 'allowBackToBack'],
                                data.checked
                            )
                        }
                    />
                </div>
            </Card>

            {/* Language Preferences */}
            <Card style={{ padding: '20px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '16px' }}>
                    Language Preferences
                </Text>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '12px',
                    }}
                >
                    {languages.map((lang) => (
                        <Checkbox
                            key={lang}
                            label={lang}
                            checked={preferences?.preferredLanguages?.includes(lang) || false}
                            onChange={(_, data) => {
                                const currentLangs = preferences?.preferredLanguages || [];
                                const newLangs = data.checked
                                    ? [...currentLangs, lang]
                                    : currentLangs.filter((l) => l !== lang);
                                updatePreferences(['preferredLanguages'], newLangs);
                            }}
                        />
                    ))}
                </div>
            </Card>
        </div>
    );

    const renderNotificationsTab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Email Notifications */}
            <Card style={{ padding: '20px' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                    }}
                >
                    <Alert24Regular style={{ fontSize: '20px', color: '#0078d4' }} />
                    <Text size={600} weight="semibold">
                        Email Notifications
                    </Text>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Checkbox
                        label="New consultation assignments"
                        checked={
                            preferences?.notificationPreferences?.email?.newAssignment || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'email', 'newAssignment'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Schedule changes"
                        checked={
                            preferences?.notificationPreferences?.email?.scheduleChanges || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'email', 'scheduleChanges'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Emergency consultations"
                        checked={
                            preferences?.notificationPreferences?.email?.emergencyConsults || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'email', 'emergencyConsults'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Daily schedule summary"
                        checked={
                            preferences?.notificationPreferences?.email?.dailySchedule || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'email', 'dailySchedule'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Weekly schedule summary"
                        checked={
                            preferences?.notificationPreferences?.email?.weeklySchedule || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'email', 'weeklySchedule'],
                                data.checked
                            )
                        }
                    />
                </div>
            </Card>

            {/* Teams Notifications */}
            <Card style={{ padding: '20px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '16px' }}>
                    Microsoft Teams Notifications
                </Text>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Checkbox
                        label="New consultation assignments"
                        checked={
                            preferences?.notificationPreferences?.teams?.newAssignment || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'teams', 'newAssignment'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Urgent consultations"
                        checked={
                            preferences?.notificationPreferences?.teams?.urgentConsults || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'teams', 'urgentConsults'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Schedule reminders"
                        checked={
                            preferences?.notificationPreferences?.teams?.scheduleReminders || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'teams', 'scheduleReminders'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Patient waiting notifications"
                        checked={
                            preferences?.notificationPreferences?.teams?.patientWaiting || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'teams', 'patientWaiting'],
                                data.checked
                            )
                        }
                    />
                </div>
            </Card>

            {/* Mobile Notifications */}
            <Card style={{ padding: '20px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '16px' }}>
                    Mobile Notifications
                </Text>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Checkbox
                        label="Emergency consultations only"
                        checked={
                            preferences?.notificationPreferences?.mobile?.emergencyOnly || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'mobile', 'emergencyOnly'],
                                data.checked
                            )
                        }
                    />
                    <Checkbox
                        label="Schedule reminders"
                        checked={
                            preferences?.notificationPreferences?.mobile?.scheduleReminders || false
                        }
                        onChange={(_, data) =>
                            updatePreferences(
                                ['notificationPreferences', 'mobile', 'scheduleReminders'],
                                data.checked
                            )
                        }
                    />
                </div>
            </Card>
        </div>
    );

    const renderUITab = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Theme and Layout */}
            <Card style={{ padding: '20px' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                    }}
                >
                    <Settings24Regular style={{ fontSize: '20px', color: '#0078d4' }} />
                    <Text size={600} weight="semibold">
                        Interface Preferences
                    </Text>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                    <Field label="Theme">
                        <RadioGroup
                            value={preferences?.uiPreferences?.theme}
                            onChange={(_, data) =>
                                updatePreferences(['uiPreferences', 'theme'], data.value)
                            }
                        >
                            <Radio value="light" label="Light" />
                            <Radio value="dark" label="Dark" />
                        </RadioGroup>
                    </Field>

                    <Field label="Dashboard Layout">
                        <RadioGroup
                            value={preferences?.uiPreferences?.dashboardLayout}
                            onChange={(_, data) =>
                                updatePreferences(['uiPreferences', 'dashboardLayout'], data.value)
                            }
                        >
                            <Radio value="standard" label="Standard" />
                            <Radio value="compact" label="Compact" />
                            <Radio value="detailed" label="Detailed" />
                        </RadioGroup>
                    </Field>

                    <Field label="Calendar View">
                        <RadioGroup
                            value={preferences?.uiPreferences?.calendarView}
                            onChange={(_, data) =>
                                updatePreferences(['uiPreferences', 'calendarView'], data.value)
                            }
                        >
                            <Radio value="day" label="Day" />
                            <Radio value="week" label="Week" />
                            <Radio value="month" label="Month" />
                        </RadioGroup>
                    </Field>
                </div>
            </Card>

            {/* Display Options */}
            <Card style={{ padding: '20px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '16px' }}>
                    Display Options
                </Text>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Text>Auto-refresh dashboard</Text>
                        <Switch
                            checked={preferences?.uiPreferences?.autoRefresh || false}
                            onChange={(_, data) =>
                                updatePreferences(['uiPreferences', 'autoRefresh'], data.checked)
                            }
                        />
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Text>Notification sounds</Text>
                        <Switch
                            checked={preferences?.uiPreferences?.notificationSounds || false}
                            onChange={(_, data) =>
                                updatePreferences(
                                    ['uiPreferences', 'notificationSounds'],
                                    data.checked
                                )
                            }
                        />
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Text>Compact view</Text>
                        <Switch
                            checked={preferences?.uiPreferences?.compactView || false}
                            onChange={(_, data) =>
                                updatePreferences(['uiPreferences', 'compactView'], data.checked)
                            }
                        />
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Text>Show patient photos</Text>
                        <Switch
                            checked={preferences?.preferences?.showPatientPhotos || false}
                            onChange={(_, data) =>
                                updatePreferences(
                                    ['preferences', 'showPatientPhotos'],
                                    data.checked
                                )
                            }
                        />
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Text>Auto-accept assignments</Text>
                        <Switch
                            checked={preferences?.preferences?.autoAcceptAssignments || false}
                            onChange={(_, data) =>
                                updatePreferences(
                                    ['preferences', 'autoAcceptAssignments'],
                                    data.checked
                                )
                            }
                        />
                    </div>
                </div>
            </Card>

            {/* Consultation Reminders */}
            <Card style={{ padding: '20px' }}>
                <Text size={600} weight="semibold" style={{ marginBottom: '16px' }}>
                    Consultation Reminders
                </Text>

                <Field label="Pre-consultation reminder (minutes before)">
                    <Slider
                        min={0}
                        max={60}
                        step={5}
                        value={preferences?.preferences?.preConsultReminder || 10}
                        onChange={(_, data) =>
                            updatePreferences(['preferences', 'preConsultReminder'], data.value)
                        }
                    />
                    <Text size={300} style={{ color: '#605e5c' }}>
                        {preferences?.preferences?.preConsultReminder || 10} minutes before
                    </Text>
                </Field>
            </Card>
        </div>
    );

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                }}
            >
                <Spinner size="large" label="Loading preferences..." />
            </div>
        );
    }

    return (
        <div style={{ padding: '0' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <div>
                        <Text size={900} weight="bold">
                            Doctor Preferences
                        </Text>
                        <Text
                            size={300}
                            style={{ display: 'block', color: '#605e5c', marginTop: '4px' }}
                        >
                            Configure your schedule, consultation preferences, and notification
                            settings
                        </Text>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Button
                            appearance="secondary"
                            icon={<ArrowCounterclockwise24Regular />}
                            onClick={handleReset}
                            disabled={saving}
                        >
                            Reset to Defaults
                        </Button>
                        <Button
                            appearance="primary"
                            icon={<Save24Regular />}
                            onClick={handleSave}
                            disabled={!hasChanges || saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                </div>

                {hasChanges && (
                    <div style={{ marginTop: '12px' }}>
                        <MessageBar intent="warning">
                            <MessageBarBody>
                                <Warning24Regular style={{ marginRight: '8px' }} />
                                You have unsaved changes. Don't forget to save your preferences.
                            </MessageBarBody>
                        </MessageBar>
                    </div>
                )}

                {message && (
                    <div style={{ marginTop: '12px' }}>
                        <MessageBar
                            intent={
                                message.type === 'success'
                                    ? 'success'
                                    : message.type === 'error'
                                    ? 'error'
                                    : 'warning'
                            }
                        >
                            <MessageBarBody>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {message.type === 'success' && (
                                            <Checkmark24Regular style={{ marginRight: '8px' }} />
                                        )}
                                        {message.text}
                                    </div>
                                    <Button
                                        appearance="transparent"
                                        size="small"
                                        onClick={() => setMessage(null)}
                                        style={{ minWidth: 'auto', padding: '4px' }}
                                    >
                                        ×
                                    </Button>
                                </div>
                            </MessageBarBody>
                        </MessageBar>
                    </div>
                )}
            </div>

            {/* Doctor Info */}
            {preferences && (
                <Card style={{ padding: '20px', marginBottom: '24px', backgroundColor: '#f8f9fa' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <div>
                            <Text size={600} weight="semibold">
                                {preferences.name}
                            </Text>
                            <Text size={400} style={{ display: 'block', color: '#605e5c' }}>
                                {preferences.specialty} • Time Zone: {preferences.timeZone}
                            </Text>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Badge appearance="tint" color="brand">
                                Max {preferences.maxDailyConsultations} consultations/day
                            </Badge>
                            {preferences.emergencyAvailable && (
                                <Badge appearance="tint" color="success">
                                    Emergency Available
                                </Badge>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            {/* Navigation Tabs */}
            <TabList
                selectedValue={activeTab}
                onTabSelect={(_, data) => setActiveTab(data.value as string)}
                style={{ marginBottom: '24px' }}
            >
                <Tab value="schedule" icon={<Clock24Regular />}>
                    Schedule & Availability
                </Tab>
                <Tab value="consultation" icon={<Person24Regular />}>
                    Consultation Preferences
                </Tab>
                <Tab value="notifications" icon={<Alert24Regular />}>
                    Notifications
                </Tab>
                <Tab value="ui" icon={<Settings24Regular />}>
                    Interface & Display
                </Tab>
            </TabList>

            {/* Tab Content */}
            {activeTab === 'schedule' && renderScheduleTab()}
            {activeTab === 'consultation' && renderConsultationTab()}
            {activeTab === 'notifications' && renderNotificationsTab()}
            {activeTab === 'ui' && renderUITab()}

            {/* Save Progress */}
            {saving && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        left: '20px',
                        zIndex: 1000,
                    }}
                >
                    <Card style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Spinner size="small" />
                            <Text>Saving preferences...</Text>
                        </div>
                        <ProgressBar style={{ marginTop: '8px' }} />
                    </Card>
                </div>
            )}
        </div>
    );
};

export default DoctorPreferences;
